import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  TrendingDown, TrendingUp, AlertTriangle, CheckCircle, 
  DollarSign, Users, Calendar, RefreshCcw, Loader2, Target,
  Eye, UserCheck, MessageSquare, UserPlus, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const TeamPerformancePage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState([]);
  const [periodType, setPeriodType] = useState('daily');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);

  const fetchTeamPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/team-performance?period_type=${periodType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTeamData(data.dealers || []);
      } else {
        toast.error('Failed to load team data');
      }
    } catch (err) {
      console.error('Error fetching team performance:', err);
      toast.error('Failed to load team performance');
    } finally {
      setLoading(false);
    }
  }, [token, periodType]);

  useEffect(() => {
    fetchTeamPerformance();
  }, [fetchTeamPerformance]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (percent) => {
    if (percent >= 100) return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>;
    if (percent >= 75) return <Badge className="bg-amber-600">{percent}%</Badge>;
    return <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{percent}%</Badge>;
  };

  const getProgressBar = (percent) => {
    const color = percent >= 100 ? 'bg-emerald-500' : percent >= 75 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="w-16 bg-slate-700 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${Math.min(percent || 0, 100)}%` }} />
      </div>
    );
  };

  // Calculate team summary stats
  const totalInShortfall = teamData.filter(d => d.overall_status === 'red').length;
  const totalOnTrack = teamData.filter(d => d.overall_status === 'green').length;
  const totalBrokerageTarget = teamData.reduce((sum, d) => sum + (d.shortfalls?.brokerage?.target || 0), 0);
  const totalBrokerageAchieved = teamData.reduce((sum, d) => sum + (d.shortfalls?.brokerage?.achieved || 0), 0);
  const totalBrokerageShortfall = teamData.reduce((sum, d) => sum + (d.shortfalls?.brokerage?.shortfall || 0), 0);
  const totalTradedShortfall = teamData.reduce((sum, d) => sum + (d.shortfalls?.traded_clients?.shortfall || 0), 0);

  const handleViewDetails = (dealer) => {
    setSelectedDealer(dealer);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-performance-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Team Performance</h1>
          <p className="text-slate-400">Monitor performance and shortfalls of your dealers</p>
        </div>
        <div className="flex gap-2">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-40 bg-slate-800 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchTeamPerformance} className="border-[#C5A059]/30">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Team Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-dark border-white/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-xs text-slate-400">My Dealers</p>
                <p className="text-lg font-bold text-white">{teamData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-red-500/30 bg-red-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-red-400">In Shortfall</p>
                <p className="text-lg font-bold text-red-400">{totalInShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-xs text-emerald-400">On Track</p>
                <p className="text-lg font-bold text-emerald-400">{totalOnTrack}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-[#C5A059]/30 bg-[#C5A059]/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C5A059]" />
              <div>
                <p className="text-xs text-[#C5A059]">Team Target</p>
                <p className="text-lg font-bold text-[#C5A059]">{formatCurrency(totalBrokerageTarget)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-xs text-amber-400">Total Shortfall</p>
                <p className="text-lg font-bold text-amber-400">{formatCurrency(totalBrokerageShortfall)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Aggregate Progress */}
      <Card className="card-dark border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-lg">Team Brokerage Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Achieved: {formatCurrency(totalBrokerageAchieved)}</span>
              <span className="text-slate-400">Target: {formatCurrency(totalBrokerageTarget)}</span>
            </div>
            <Progress 
              value={totalBrokerageTarget > 0 ? Math.min((totalBrokerageAchieved / totalBrokerageTarget) * 100, 100) : 0} 
              className="h-3"
            />
            <div className="text-center text-sm">
              <span className={totalBrokerageAchieved >= totalBrokerageTarget ? 'text-emerald-400' : 'text-amber-400'}>
                {totalBrokerageTarget > 0 ? Math.round((totalBrokerageAchieved / totalBrokerageTarget) * 100) : 0}% Complete
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dealers Table */}
      {teamData.length === 0 ? (
        <Card className="card-dark border-white/5">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-white text-lg font-medium mb-2">No Dealers Mapped</h3>
            <p className="text-slate-400">You don't have any dealers reporting to you yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white">Dealer Performance</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-400">Dealer</TableHead>
                  <TableHead className="text-slate-400 text-center">Traded Clients</TableHead>
                  <TableHead className="text-slate-400 text-center">Brokerage</TableHead>
                  <TableHead className="text-slate-400 text-center">Client Activation</TableHead>
                  {periodType === 'daily' && (
                    <TableHead className="text-slate-400 text-center">Tier Trade</TableHead>
                  )}
                  <TableHead className="text-slate-400 text-center">New Clients</TableHead>
                  <TableHead className="text-slate-400 text-center">Meetings</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-right text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamData.map((dealer) => {
                  const traded = dealer.shortfalls?.traded_clients || {};
                  const brokerage = dealer.shortfalls?.brokerage || {};
                  const activation = dealer.shortfalls?.client_activation || {};
                  const tierTrade = dealer.shortfalls?.tier_trade || {};
                  const newClients = dealer.shortfalls?.new_clients || {};
                  const meetings = dealer.shortfalls?.meetings || {};

                  return (
                    <TableRow key={dealer.user_id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <div>
                          <span className="text-white font-medium">{dealer.user_name}</span>
                          {dealer.user_code && (
                            <span className="text-slate-500 text-xs block">{dealer.user_code}</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Traded Clients */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-white text-sm">{traded.achieved || 0}/{traded.target || 0}</span>
                          {getProgressBar(traded.percent_achieved)}
                          {traded.shortfall > 0 && (
                            <span className="text-red-400 text-xs">-{traded.shortfall}</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Brokerage */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-white text-sm">{formatCurrency(brokerage.achieved)}</span>
                          <span className="text-slate-500 text-xs">/ {formatCurrency(brokerage.target)}</span>
                          {getProgressBar(brokerage.percent_achieved)}
                          {brokerage.shortfall > 0 && (
                            <span className="text-red-400 text-xs">-{formatCurrency(brokerage.shortfall)}</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Client Activation */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-white text-sm">{activation.achieved || 0}/{activation.target || 0}</span>
                          {getProgressBar(activation.percent_achieved)}
                          {activation.shortfall > 0 && (
                            <span className="text-red-400 text-xs">-{activation.shortfall}</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Tier Trade (Daily only) */}
                      {periodType === 'daily' && (
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center text-xs">
                            <span className="text-cyan-400">P:{tierTrade.platinum?.achieved || 0}/{tierTrade.platinum?.target || 0}</span>
                            <span className="text-yellow-400">G:{tierTrade.gold?.achieved || 0}/{tierTrade.gold?.target || 0}</span>
                            <span className="text-gray-300">S:{tierTrade.silver?.achieved || 0}/{tierTrade.silver?.target || 0}</span>
                            <span className="text-blue-400">B:{tierTrade.blue?.achieved || 0}/{tierTrade.blue?.target || 0}</span>
                          </div>
                        </TableCell>
                      )}
                      {/* New Clients - Always shown */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-white text-sm">{newClients.achieved || 0}/{newClients.target || 0}</span>
                          {getProgressBar(newClients.percent_achieved)}
                          {newClients.shortfall > 0 && (
                            <span className="text-red-400 text-xs">-{newClients.shortfall}</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Meetings - Always shown */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-white text-sm">{meetings.achieved || 0}/{meetings.target || 0}</span>
                          {getProgressBar(meetings.percent_achieved)}
                          {meetings.shortfall > 0 && (
                            <span className="text-red-400 text-xs">-{meetings.shortfall}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {dealer.overall_status === 'green' ? (
                          <Badge className="bg-emerald-600">
                            <CheckCircle className="w-3 h-3 mr-1" /> On Track
                          </Badge>
                        ) : (
                          <Badge className="bg-red-600">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Shortfall
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDetails(dealer)}
                          className="hover:bg-white/10"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C5A059]" />
              {selectedDealer?.user_name} - {periodType.charAt(0).toUpperCase() + periodType.slice(1)} Performance
            </DialogTitle>
          </DialogHeader>

          {selectedDealer && (
            <div className="space-y-4">
              {/* Traded Clients */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Traded Clients
                  </span>
                  {getStatusBadge(selectedDealer.shortfalls?.traded_clients?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{selectedDealer.shortfalls?.traded_clients?.target || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{selectedDealer.shortfalls?.traded_clients?.achieved || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{selectedDealer.shortfalls?.traded_clients?.shortfall || 0}</p>
                  </div>
                </div>
              </div>

              {/* Brokerage */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Brokerage
                  </span>
                  {getStatusBadge(selectedDealer.shortfalls?.brokerage?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{formatCurrency(selectedDealer.shortfalls?.brokerage?.target)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{formatCurrency(selectedDealer.shortfalls?.brokerage?.achieved)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{formatCurrency(selectedDealer.shortfalls?.brokerage?.shortfall)}</p>
                  </div>
                </div>
              </div>

              {/* Client Activation */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <UserCheck className="w-4 h-4" /> Client Activation
                  </span>
                  {getStatusBadge(selectedDealer.shortfalls?.client_activation?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{selectedDealer.shortfalls?.client_activation?.target || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{selectedDealer.shortfalls?.client_activation?.achieved || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{selectedDealer.shortfalls?.client_activation?.shortfall || 0}</p>
                  </div>
                </div>
              </div>

              {/* Tier Trade (Daily) */}
              {periodType === 'daily' && selectedDealer.shortfalls?.tier_trade && (
                <div className="bg-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 flex items-center gap-2">
                      <Layers className="w-4 h-4" /> Tier Trade Targets
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    {['platinum', 'gold', 'silver', 'blue'].map((tier) => {
                      const data = selectedDealer.shortfalls?.tier_trade?.[tier] || {};
                      const colors = { platinum: 'text-cyan-400', gold: 'text-yellow-400', silver: 'text-gray-300', blue: 'text-blue-400' };
                      return (
                        <div key={tier} className="text-center">
                          <p className={`${colors[tier]} font-medium capitalize`}>{tier}</p>
                          <p className="text-white">{data.achieved || 0}/{data.target || 0}</p>
                          {data.shortfall > 0 && <p className="text-red-400 text-xs">-{data.shortfall}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Monthly: New Clients & Meetings */}
              {periodType === 'monthly' && (
                <>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> New Clients
                      </span>
                      {getStatusBadge(selectedDealer.shortfalls?.new_clients?.percent_achieved || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Target</p>
                        <p className="text-white font-bold">{selectedDealer.shortfalls?.new_clients?.target || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Achieved</p>
                        <p className="text-white font-bold">{selectedDealer.shortfalls?.new_clients?.achieved || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Shortfall</p>
                        <p className="text-red-400 font-bold">{selectedDealer.shortfalls?.new_clients?.shortfall || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Meetings
                      </span>
                      {getStatusBadge(selectedDealer.shortfalls?.meetings?.percent_achieved || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Target</p>
                        <p className="text-white font-bold">{selectedDealer.shortfalls?.meetings?.target || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Achieved</p>
                        <p className="text-white font-bold">{selectedDealer.shortfalls?.meetings?.achieved || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Shortfall</p>
                        <p className="text-red-400 font-bold">{selectedDealer.shortfalls?.meetings?.shortfall || 0}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPerformancePage;
